import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Inbox } from 'lucide-react';
import { ContactCard } from './ContactCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tabs = [
  { id: '', label: 'Alle' },
  { id: 'new', label: 'Neu' },
  { id: 'in_progress', label: 'In Bearbeitung' },
  { id: 'done', label: 'Erledigt' },
];

export function AdminDashboard({ token, onUnauthorized }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/contacts`, auth);
      setItems(data);
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    const { data } = await axios.patch(`${API}/admin/contacts/${id}/status`, { status }, auth);
    setItems((list) => list.map((c) => (c.id === id ? data : c)));
  };

  const remove = async (id) => {
    if (!window.confirm('Anfrage endgültig löschen?')) return;
    await axios.delete(`${API}/admin/contacts/${id}`, auth);
    setItems((list) => list.filter((c) => c.id !== id));
  };

  const saveNotes = async (id, notes) => {
    const { data } = await axios.patch(`${API}/admin/contacts/${id}/notes`, { notes }, auth);
    setItems((list) => list.map((c) => (c.id === id ? data : c)));
  };

  const counts = items.reduce((acc, c) => ({ ...acc, [c.status]: (acc[c.status] || 0) + 1 }), {});
  const visible = filter ? items.filter((c) => c.status === filter) : items;

  return (
    <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8 md:py-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#0B1E44]">Kundenanfragen</h1>
          <p className="text-slate-600 mt-1 text-sm">{items.length} Anfragen insgesamt · {counts.new || 0} neu</p>
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="admin-filter-tabs">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setFilter(t.id)} className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${filter === t.id ? 'bg-[#0B1E44] text-white border-[#0B1E44]' : 'bg-white text-[#0B1E44] border-slate-200 hover:border-[#B8892A]'}`} data-testid={`admin-filter-${t.id || 'all'}`}>
              {t.label}{t.id && counts[t.id] ? ` (${counts[t.id]})` : ''}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#B8892A]" /></div>
      ) : visible.length === 0 ? (
        <div className="mt-10 bg-white rounded-2xl border border-slate-100 card-shadow p-14 text-center text-slate-500" data-testid="admin-empty">
          <Inbox className="w-10 h-10 mx-auto text-slate-300" />
          <div className="mt-3 font-semibold text-[#0B1E44]">Keine Anfragen in dieser Ansicht</div>
        </div>
      ) : (
        <div className="mt-8 grid gap-5" data-testid="admin-contact-list">
          {visible.map((c) => <ContactCard key={c.id} contact={c} token={token} onStatus={updateStatus} onDelete={remove} onNotes={saveNotes} />)}
        </div>
      )}
    </main>
  );
}
